import { CheckIcon } from "@xola/icons";
import { Badge, Button, flash, SubmitButton } from "@xola/ui-kit";
import { delay, get, isEmpty, without } from "lodash";
import React, { useEffect, useState } from "react";
import { useMutation } from "react-query";
import { AccountLink, RemoteGatewayType, Seller, StripeAccount } from "types";
import { api } from "../../../api/api";
import { useSeller } from "../../../hooks/useAccount";
import { useInvalidate } from "../../../hooks/useInvalidation";
import { useResource } from "../../../hooks/useResource";
import { ContentLayout } from "../../Layout/ContentLayout";

//
// @see: https://github.com/xola/xola/blob/master/src/Xola/SellerBundle/Resources/public/js/view/settings/stripe/stripeConnectHostedOnboarding.js
// @see: https://github.com/xola/xola/blob/master/src/Xola/SellerBundle/Resources/public/js/model/stripe/stripeAccount.js
//

export const StripePaymentGateway = () => {
    const seller = useSeller();
    const invalidate = useInvalidate();
    const [linkExpiresAt, setLinkExpiresAt] = useState<number | null>(null);

    const { stripeAccount } = seller;
    const requiresKYC = stripeAccount ? isKYCNeeded(stripeAccount) : false;
    const requiresBankAccount = stripeAccount
        ? getRequirementsCurrentlyDue(stripeAccount)?.includes("external_account")
        : true;

    const hasLinkExpired = Boolean(linkExpiresAt && linkExpiresAt < Date.now());
    const needsLink = requiresKYC || (requiresKYC && hasLinkExpired);
    const {
        data: accountLink,
        isLoading,
        refetch,
    } = useResource<AccountLink>(["sellers", seller.id, "stripeAccount", "accountLink"], null, { enabled: needsLink });

    useEffect(() => {
        if (accountLink?.expiresAt) {
            const value = accountLink.expiresAt * 1000;
            if (value !== linkExpiresAt) {
                setLinkExpiresAt(value);
            }
        }
    }, [isLoading, accountLink, linkExpiresAt]);

    const { mutateAsync: updatePaymentGateway, isLoading: isSubmitting } = useMutation(async () => {
        return api.patch(`sellers/${seller.id}`, { defaultGateway: "stripe" });
    });

    const handleUpdateKYC = () => {
        const features = ["resizable=yes", "scrollbars=yes", "status=yes", "width=475", "height=630"];
        const popupWindow = window.open(accountLink!.url, "", features.join(","));
        setLinkExpiresAt(Date.now()); // Expire the link as expire once it's opened because it's one-time use
        delay(trackWindow, 2000, popupWindow);
        delay(refetch, 1000);
    };

    const trackWindow = async (kycWindow: Window) => {
        if (!kycWindow) {
            return;
        }

        try {
            // If the domain is different than our own, this will throw an exception
            if (kycWindow.location.host === window.location.host) {
                // If we reach here, the domain has changed to our own, close the window & fetch the stripe account info
                await syncStripeAccount(seller);
                invalidate("sellers", seller.id);
                delay(kycWindow.close, 3000);
                return;
            }

            if (kycWindow.location.host === undefined) {
                // Window was manually closed
                invalidate("sellers", seller.id);
                return;
            }
        } catch (error) {
            // The domain hasn't changed. Check again in some time
            // Log this one for now till we figure out cross-domain issue
            console.log(error);
        }

        return delay(trackWindow, 3000, kycWindow);
    };

    const handleSubmit = async () => {
        await updatePaymentGateway();
        invalidate("sellers", seller.id);
        flash.success("Payment gateway set to Stripe");
    };

    const showSubmitButton = seller.defaultGateway?.type !== RemoteGatewayType.STRIPE_XOLA;

    return (
        <ContentLayout.Section
            title="KYC"
            description="KYC stands for “Know Your Customer” and is a set of financial regulations that require processors to verify the identity of individuals and entities that process payments and receive payouts. "
        >
            {showSubmitButton && !requiresKYC ? (
                <SubmitButton isLoading={isSubmitting} onClick={handleSubmit}>
                    Submit
                </SubmitButton>
            ) : (
                <div className="space-y-4">
                    <div className="flex items-center space-x-2">
                        {requiresKYC ? (
                            <Badge color="danger">Verification Required</Badge>
                        ) : (
                            <Badge icon={<CheckIcon />} color="success">
                                Verified
                            </Badge>
                        )}
                    </div>

                    <div>
                        {requiresKYC ? (
                            <div className="space-y-2">
                                <div>
                                    In order to process payments and receive payouts from Xola, you must complete our
                                    payment processor&apos;s KYC form and add your bank account info.
                                </div>
                                <Button color="success" disabled={isLoading} onClick={handleUpdateKYC}>
                                    Update KYC
                                </Button>
                            </div>
                        ) : (
                            <div>
                                Your account is verified with our payment processor{" "}
                                {requiresBankAccount && (
                                    <span>however your bank account information is required to receive payouts</span>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </ContentLayout.Section>
    );
};

/**
 * Returns true if any kind of documentation is still required for KYC.
 * "external_accounts" are only required for payouts, and not KYC, so they are removed from this requirement.
 *
 * @returns {boolean}
 */
export const isKYCNeeded = (stripeAccount: StripeAccount): boolean => {
    const verification = getRemoteData(stripeAccount, "verification", null);
    const requirements = getRemoteData(stripeAccount, "requirements", null);
    const futureRequirements = getRemoteData(stripeAccount, "future_requirements", null);

    if (requirements || futureRequirements) {
        // This seller has new data. We dont care about the verification attribute anymore
        const currentlyDue = without(getRequirementsCurrentlyDue(stripeAccount), "external_account");
        const futureRequirementsCurrentlyDue = without(
            getFutureRequirementsCurrentlyDue(stripeAccount),
            "external_account",
        );
        return !isEmpty(currentlyDue) || !isEmpty(futureRequirementsCurrentlyDue);
    }

    if (verification && !isEmpty(verification.disabled_reason)) {
        // This an old account which still uses the 'verification' attribute
        return true;
    }

    return false;
};

const getRemoteData = (stripeAccount: StripeAccount, key: string, defaultValue: any) => {
    return get(stripeAccount, `data.${key}`, defaultValue);
};

const getRequirementsCurrentlyDue = (stripeAccount: StripeAccount) => {
    return get(stripeAccount, `data.requirements.currently_due`, []);
};

const getFutureRequirementsCurrentlyDue = (stripeAccount: StripeAccount) => {
    return get(stripeAccount, `data.future_requirements.currently_due`, []);
};

const syncStripeAccount = async (seller: Seller) => {
    await api.get<Seller>(`sellers/${seller.id}/stripeAccount`);
};
